'use client';

import { useAwaitQuery } from '@/common/hooks/useAwaitQuery';
import { useDropdown } from '@/common/hooks/useDropdown';
import { useTypePolicies } from '@/common/hooks/useTypePolicies';
import DEFAULT_OPERATIONS from '@/components/CreateAccount/createAccount.gql';
import { GET_ITEM_COUNT_QUERY } from '@/components/Header/cartTrigger.gql';
import StoreConfig from '@/config';
import { useCartContext } from '@/context/cart';
import { useUserContext } from '@/context/user';
import { useCartTrigger } from '@/hooks/Header/useCartTrigger';
import { BrowserPersistence } from '@/util';
import { ApolloClient, HttpLink, InMemoryCache, useMutation } from '@apollo/client';
import qs from 'qs';
import { useCallback, useEffect, useState } from 'react';
import { CUSTOM_TYPES } from './currencySwitcher.gql';

const storage = new BrowserPersistence();

export const useCurrencySwitcher = (props = {}) => {
  const { typePolicies = CUSTOM_TYPES } = props;

  useTypePolicies(typePolicies);

  const [{ cartId }, { getCartDetails, createCart, removeCart }] = useCartContext();

  const { itemCount } = useCartTrigger({
    queries: {
      getItemCountQuery: GET_ITEM_COUNT_QUERY,
    },
  });

  const [{ isSignedIn }, { getUserDetails, setToken }] = useUserContext();

  const { mergeCartsMutation, getCartDetailsQuery, createCartMutation, mergeGuestCartsMutation, switchStoreMutation } =
    DEFAULT_OPERATIONS;

  const [mergeCarts] = useMutation(mergeCartsMutation);

  const [fetchCartId] = useMutation(createCartMutation);

  const [mergeGuestCarts] = useMutation(mergeGuestCartsMutation);

  const fetchItemCount = useAwaitQuery(GET_ITEM_COUNT_QUERY);

  const availableCountries = StoreConfig.available_countries;

  const currentCountryCode = StoreConfig.current_country_code;

  const handleSwitchCountry = useCallback(
    async countryCode => {
      const targetCountry = availableCountries.find(temp => temp.code === countryCode);

      const currentCountry = availableCountries.find(temp => temp.code === currentCountryCode);

      const signin_token = storage.getItem('signin_token');

      if (isSignedIn) {
        // 已登录

        const currentUrl = window.location.href;
        const url = new URL(currentUrl);

        // 获取当前的查询参数
        const queryParams = new URLSearchParams(url.search);

        queryParams.set('from_country', currentCountryCode);
        queryParams.set('token', signin_token);

        if (itemCount > 0) {
          // 购物车不为空
          queryParams.set('cart_id', cartId);
          // 清空购物车

          await removeCart();

          await createCart({
            fetchCartId,
          });
        }

        // 更新 URL 的查询参数
        url.search = queryParams.toString();

        const targetUrl = url.toString().replace(currentCountry.url, targetCountry.url);

        window.location.href = targetUrl;
      } else {
        const currentUrl = window.location.href;
        const url = new URL(currentUrl);

        // 获取当前的查询参数
        const queryParams = new URLSearchParams(url.search);

        queryParams.set('from_country', currentCountryCode);

        if (itemCount > 0) {
          // 购物车不为空
          queryParams.set('cart_id', cartId);

          // 清空购物车
          await removeCart();

          await createCart({
            fetchCartId,
          });
        }

        // 更新 URL 的查询参数
        url.search = queryParams.toString();

        const targetUrl = url.toString().replace(currentCountry.url, targetCountry.url);

        window.location.href = targetUrl;
      }
    },
    [availableCountries, isSignedIn, currentCountryCode, itemCount, cartId, removeCart, createCart, fetchCartId],
  );

  useEffect(() => {
    const syncCarts = async () => {
      const queryString = window.location.search;
      const parsedParams = qs.parse(queryString, { ignoreQueryPrefix: true });

      const { cart_id, from_country, token } = parsedParams;

      if (cart_id && from_country && from_country !== currentCountryCode) {
        // 购物车有产品，需要执行购物车合并逻辑
        if (isSignedIn) {
          // 当前站点已登录，直接合并购物车
          await mergeCarts({
            variables: {
              destinationCartId: cartId,
              sourceCartId: cart_id,
            },
          });

          const cartUpdateCountEvent = new Event('CART_UPDATE_COUNT', {
            bubbles: true,
          });

          globalThis.document.dispatchEvent(cartUpdateCountEvent);
        } else {
          if (token && !isSignedIn) {
            // 同步登录状态
            await setToken(token);
            await getUserDetails();
          }

          //  同步获取购物车数量
          const { data } = await fetchItemCount({
            variables: {
              cartId,
            },
          });

          const cartCount = data.cart.total_quantity;

          const headers = {
            store: from_country,
            authorization: token ? `Bearer ${token}` : '',
          };

          const client = new ApolloClient({
            link: new HttpLink({
              uri: '/api/graphql',
              headers,
            }),
            cache: new InMemoryCache(),
          });

          if (cartCount === 0) {
            //  当前站点购物车为空，切换购物车
            await client.mutate({
              mutation: switchStoreMutation,
              variables: {
                cart_id: cart_id,
                store_code: currentCountryCode,
              },
            });

            // 修改cartId
            storage.setItem('cartId', cart_id);
          } else {
            //  当前站点购物车不为空，合并购物车

            await mergeGuestCarts({
              variables: {
                sourceCartId: cart_id,
                targetCartId: cartId,
              },
            });
          }

          const cartUpdateCountEvent = new Event('CART_UPDATE_COUNT', {
            bubbles: true,
          });

          globalThis.document.dispatchEvent(cartUpdateCountEvent);
        }

        const url = new URL(window.location.href);

        url.searchParams.delete('from_country');
        url.searchParams.delete('cart_id');
        url.searchParams.delete('token');

        window.history.replaceState({}, '', url);
      } else if (from_country && from_country !== currentCountryCode) {
        // 不需要执行购物车合并逻辑
        if (token && !isSignedIn) {
          // 同步登录状态
          await setToken(token);
          await getUserDetails();
        }

        const url = new URL(window.location.href);

        url.searchParams.delete('from_country');
        url.searchParams.delete('cart_id');
        url.searchParams.delete('token');

        window.history.replaceState({}, '', url);
      }
    };

    syncCarts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    elementRef: currencyMenuRef,
    expanded: currencyMenuIsOpen,
    setExpanded: setCurrencyMenuIsOpen,
    triggerRef: currencyMenuTriggerRef,
  } = useDropdown();

  const [currencyMobileMenuIsOpen, setCurrencyMobileMenuIsOpen] = useState(false);

  const handleTriggerClick = useCallback(() => {
    setCurrencyMenuIsOpen(isOpen => !isOpen);

    setCurrencyMobileMenuIsOpen(isOpen => !isOpen);
  }, [setCurrencyMenuIsOpen]);

  return {
    availableCountries,
    currentCountryCode,
    currencyMenuRef,
    currencyMenuTriggerRef,
    currencyMenuIsOpen,
    currencyMobileMenuIsOpen,
    handleTriggerClick,
    handleSwitchCountry,
  };
};
